// @ts-strict-ignore
import type { IconName } from '@usealloy/component-library';
import { SYSTEM_OUTCOMES_DEFAULT_USAGES_MAP } from '../constants';
import type {
  CreateOutcomePayload,
  OutcomeFormValues,
  OutcomeUsageOption,
  OutcomeWithUsage,
} from '../types';

export const getAvailableNewOutcomeUsage = (flags: {
  isJourneysEnabled: boolean;
  isInvestigationsEnabled: boolean;
  isAlertGenerationInJourneysEnabled: boolean;
  isInvestigationStatusEnabled?: boolean;
}): OutcomeUsageOption[] => {
  const {
    isJourneysEnabled,
    isInvestigationsEnabled,
    isAlertGenerationInJourneysEnabled,
    isInvestigationStatusEnabled,
  } = flags;
  return [...(isJourneysEnabled ? [{
    label: 'Application Outcome',
    value: 'is_application_outcome',
  }] : []),
  ...(isAlertGenerationInJourneysEnabled ? [{
    label: 'Alert Outcome',
    value: 'is_alert_outcome',
  }] : []),
  ...(isInvestigationsEnabled ? [{
    label: 'Investigation Outcome',
    value: 'is_investigation_outcome',
  }, ...(isInvestigationStatusEnabled ? [{
    label: 'Investigation Status',
    value: 'is_investigation_status',
  }] : [])] : []), {
    label: isJourneysEnabled ? 'Workflow/Journey Actions Outcome' : 'Workflow Outcome',
    value: 'is_workflow_outcome',
  }];
};

export const transformOutcomeFormData = (formData: OutcomeFormValues) => {
  const transformedData: CreateOutcomePayload = {
    icon_name: formData.icon,
    outcome: formData.displayName,
    outcome_classification: formData.classification,
    outcome_color: formData.color,
    // if there's no description, explicitly send null instead of the form's default empty string
    description: formData.description || null,
    is_case_status: formData.caseManagementStatus,
  };

  formData.uses.forEach(({ value: usageType }) => {
    transformedData[usageType] = true;
  });

  [
    'is_alert_outcome',
    'is_application_outcome',
    'is_investigation_outcome',
    'is_investigation_status',
    'is_workflow_outcome',
  ].forEach(usageType => {
    if (!transformedData[usageType]) {
      transformedData[usageType] = false;
    }
  });

  transformedData.is_investigation_status = Boolean(
    formData.investigationStatus?.nonEscalated,
  );

  transformedData.is_escalated_investigation_status = Boolean(
    formData.investigationStatus?.escalated,
  );

  return transformedData;
};

export const transformOutcomeToFormData = (
  outcome: OutcomeWithUsage,
  usageOptions,
): OutcomeFormValues => {
  const mappedUsageOptions = usageOptions.filter(({ value }) => outcome[value]);
  return {
    uses: mappedUsageOptions,
    displayName: outcome.outcome,
    description: outcome.description,
    color: outcome.outcome_color,
    icon: outcome.icon_name as IconName,
    classification: outcome.outcome_classification,
    caseManagementStatus: outcome.is_case_status,
    investigationStatus: {
      nonEscalated: Boolean(outcome.is_investigation_status),
      escalated: Boolean(outcome.is_escalated_investigation_status),
    },
  };
};

export const isDefaultOutcomeUsage = (outcome: string, option: OutcomeUsageOption) => {
  const defaultUses = SYSTEM_OUTCOMES_DEFAULT_USAGES_MAP[outcome.toLowerCase()];
  return defaultUses.indexOf(option.value) > -1;
};
